// @ts-nocheck
import { useMemo } from 'react';
import dagre from 'dagre';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  MarkerType,
  getSmoothStepPath,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import '@/assets/custom/css/ivr-editor.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import FlowChartNode, { styleForNodeType } from './FlowChartNode';


function FlowEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  markerEnd,
}) {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 10,
  });

  return (
    <>
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        style={{ ...style, stroke: '#b0b7c3', strokeWidth: 1.5 }}
      />
      {data?.label && (
        <foreignObject
          width={80}
          height={22}
          x={labelX - 40}
          y={labelY - 11}
          style={{ overflow: 'visible' }}
          requiredExtensions="http://www.w3.org/1999/xhtml"
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'rgba(255,255,255,0.92)',
              borderRadius: 6,
              padding: '1px 6px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
              border: '1px solid #e2e8f0',
            }}
          >
            <span
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: '#475569',
                whiteSpace: 'nowrap',
              }}
            >
              {data.label}
            </span>
          </div>
        </foreignObject>
      )}
    </>
  );
}

const nodeTypes = { flowNode: FlowChartNode };
const edgeTypes = { smoothEdge: FlowEdge };

const pick = (obj, keys, fallback) => {
  for (const k of keys) {
    if (obj?.[k] !== undefined && obj?.[k] !== null && obj?.[k] !== '') return obj[k];
  }
  return fallback;
};


const NODE_WIDTH = 255;
const NODE_HEIGHT = 100;

function applyHorizontalLayout(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: 'LR',      
    nodesep: 60,
    ranksep: 120,
    marginx: 40,
    marginy: 40,
  });

  nodes.forEach((node) => {
    g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  return nodes.map((node) => {
    const pos = g.node(node.id);
    if (!pos) return node;
    return {
      ...node,
      position: {
        x: pos.x - NODE_WIDTH / 2,
        y: pos.y - NODE_HEIGHT / 2,
      },
    };
  });
}

function normalizeFlow(flow) {
  const rawNodes = Array.isArray(flow?.nodes) ? flow.nodes : [];
  const rawEdges = Array.isArray(flow?.edges) ? flow.edges : [];

  const ids = rawNodes.map((n, i) => String(pick(n, ['id', 'node_id', 'key'], `node-${i}`)));

  const nodes = rawNodes.map((n, i) => ({
    id: ids[i],
    type: 'flowNode',
    position: { x: 0, y: 0 },   
    data: {
      nodeType: pick(n, ['type', 'node_type', 'kind', 'category'], 'step'),
      title: pick(n, ['title', 'label', 'name', 'step'], `Step ${i + 1}`),
      subtitle: pick(n, ['description', 'subtitle', 'detail', 'message'], ''),
    },
  }));

  const edges = rawEdges.map((e, i) => {
    const source = String(pick(e, ['source', 'from', 'source_id', 'src'], ids[i] || ''));
    const target = String(
      pick(e, ['target', 'to', 'target_id', 'dst'], ids[i + 1] || ids[i] || ''),
    );
    return {
      id: pick(e, ['id'], `edge-${i}`),
      source,
      target,
      type: 'smoothEdge',
      animated: false,
      markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
      data: {
        label: pick(e, ['label', 'condition', 'event', 'trigger'], undefined),
      },
    };
  });


  const layoutNodes = applyHorizontalLayout(nodes, edges);

  return { nodes: layoutNodes, edges };
}

const LEGEND_TYPES = ['start', 'question', 'api_call', 'message', 'followup', 'human_handover', 'end'];


function FlowCanvas({ flow }) {
  const { nodes: initNodes, edges: initEdges } = useMemo(() => normalizeFlow(flow), [flow]);
  const [nodes, , onNodesChange] = useNodesState(initNodes);
  const [edges, , onEdgesChange] = useEdgesState(initEdges);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      fitView
      fitViewOptions={{ padding: 0.3, maxZoom: 0.8 }}
      minZoom={0.05}
      maxZoom={2}
      nodesDraggable
      nodesConnectable={false}
      elementsSelectable
      selectNodesOnDrag
      connectionLineStyle={{
        stroke: '#6366f1',
        strokeWidth: 1.5,
        strokeDasharray: '5 3',
      }}
      defaultEdgeOptions={{
        type: 'smoothEdge',
        markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
      }}
      proOptions={{ hideAttribution: true }}
    >
      <Background variant="dots" gap={20} size={1.5} color="#94a3b8" />
      <Controls showInteractive={false} />
      <MiniMap
        nodeColor={(node) => styleForNodeType(node.data?.nodeType).accent}
        nodeStrokeWidth={0}
        pannable
        zoomable
        position="bottom-right"
      />
    </ReactFlow>
  );
}

export default function FlowChartDialog({ open, onOpenChange, flow, agentName }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85vh] max-w-6xl flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3">
          <DialogTitle>Conversation Flow{agentName ? ` — ${agentName}` : ''}</DialogTitle>
          {flow?.summary && <DialogDescription>{flow.summary}</DialogDescription>}
        </DialogHeader>


        <div className="flex-1 min-h-0 overflow-hidden" style={{ background: '#f0f2f5' }}>
          {!flow || (Array.isArray(flow?.nodes) && flow.nodes.length === 0) ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              No flow steps returned.
            </div>
          ) : (
            <ReactFlowProvider>
              <FlowCanvas flow={flow} />
            </ReactFlowProvider>
          )}
        </div>

    
        <div className="flex flex-wrap gap-3 px-6 py-3 border-t bg-background">
          {LEGEND_TYPES.map((type) => {
            const st = styleForNodeType(type);
            return (
              <div key={type} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: st.accent }} />
                {st.label}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
